import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Plus, GitCompareArrows, X, ArrowRight, Search, ArrowUpDown, TrendingUp, TrendingDown, Zap, RotateCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import PageNavCard from "../components/PageNavCard";
import AddPlayerDialog from "../components/AddPlayerDialog";
import BackupControls from "../components/BackupControls";
import HeadshotUploader from "../components/HeadshotUploader";
import PackageBadge from "../components/PackageBadge";
import { TrainingLevelPill } from "../components/TrainingLevel";
import { TRAINING_SECTIONS } from "../lib/training";
import { api } from "../lib/api";
import { useAuth, isAdmin } from "../contexts/AuthContext";

const LEAGUES = ["ALL", "NBA", "NCAA", "WNCAA", "WNBA", "PRO", "HS"];
const LEAGUE_LABEL = { ALL: "ALL", NBA: "NBA", NCAA: "NCAA M", WNCAA: "NCAA W", WNBA: "WNBA", PRO: "PRO", HS: "HS" };

function initials(name) {
  return (name || "").split(" ").map(n => n[0]).slice(0, 2).join("").toUpperCase();
}
function headshotFor(p) {
  if (!p) return null;
  if (p.has_uploaded_headshot || p.headshot_url || p.nba_official_id) {
    return `${process.env.REACT_APP_BACKEND_URL}/api/players/${p.id}/headshot`;
  }
  return null;
}
function fmtMin(m) {
  if (m === null || m === undefined || m === "") return "—";
  if (typeof m === "number") return m.toFixed(1);
  const s = String(m);
  if (s.includes(":")) {
    const [mm, ss] = s.split(":");
    return `${Number(mm)}.${Math.round((Number(ss) || 0) * 10 / 60) || 0}`;
  }
  return s;
}
function fmtNum(v, digits = 1) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n) ? "—" : n.toFixed(digits);
}
function fmtPct(v) {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  return Number.isNaN(n) ? "—" : `${(n * 100).toFixed(1)}%`;
}
function minToNumber(m) {
  if (m === null || m === undefined || m === "") return 0;
  if (typeof m === "number") return m;
  const s = String(m);
  if (s.includes(":")) {
    const [mm, ss] = s.split(":");
    return Number(mm || 0) + Number(ss || 0) / 60;
  }
  return Number(s) || 0;
}

const COLUMNS = [
  { key: "name",       label: "Player",  align: "left",  sortable: true, get: (p, a, t) => (p.name || "").toLowerCase() },
  { key: "team",       label: "Team",    align: "left",  sortable: true, get: (p, a, t) => (p.team || "").toLowerCase() },
  { key: "position",   label: "Pos",     align: "left",  sortable: false, get: (p, a, t) => p.position || "" },
  { key: "games_played", label: "GP",    align: "right", sortable: true, get: (p, a, t) => a?.games_played ?? -Infinity },
  { key: "min",        label: "MIN",     align: "right", sortable: true, get: (p, a, t) => minToNumber(a?.min) },
  { key: "pts",        label: "PPG",     align: "right", sortable: true, get: (p, a, t) => a?.pts ?? -Infinity },
  { key: "reb",        label: "RPG",     align: "right", sortable: true, get: (p, a, t) => a?.reb ?? -Infinity },
  { key: "ast",        label: "APG",     align: "right", sortable: true, get: (p, a, t) => a?.ast ?? -Infinity },
  { key: "fg_pct",     label: "FG%",     align: "right", sortable: true, get: (p, a, t) => a?.fg_pct ?? -Infinity },
  { key: "fg3_pct",    label: "3P%",     align: "right", sortable: true, get: (p, a, t) => a?.fg3_pct ?? -Infinity },
  { key: "ft_pct",     label: "FT%",     align: "right", sortable: true, get: (p, a, t) => a?.ft_pct ?? -Infinity },
];

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [players, setPlayers] = useState([]);
  const [averages, setAverages] = useState({});
  const [trending, setTrending] = useState(null);
  const [season, setSeason] = useState(null);        // null = current season
  const [seasons, setSeasons] = useState([]);
  const [league, setLeague] = useState("ALL");
  const [teamFilter, setTeamFilter] = useState("ALL");
  const [posFilter, setPosFilter] = useState("ALL");
  const [search, setSearch] = useState("");
  const [sortKey, setSortKey] = useState("pts");
  const [sortDir, setSortDir] = useState("desc");
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [compareMode, setCompareMode] = useState(false);
  const [selected, setSelected] = useState([]);
  const [seeding, setSeeding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { players: list, averages: map, seasons: opts } = await api.playersSummary(season);
      setPlayers(list);
      setAverages(map || {});
      if (opts) setSeasons(opts);
      if (season) setTrending(null); else api.trending(5).then(setTrending).catch(() => setTrending(null));
    } catch (e) {
      toast.error("Failed to load", { description: e?.response?.data?.detail || e.message });
    } finally { setLoading(false); }
  }, [season]);
  const pastSeason = season && seasons.length && season !== seasons[0].key ? seasons.find(s => s.key === season) : null;
  useEffect(() => { load(); }, [load]);

  const runSeed = async () => {
    setSeeding(true);
    try {
      const seedRes = await api.seedPlayers();
      // After seeding, also run refresh so ESPN headshots + stats populate for any newly added players.
      const refRes = await api.refreshPlayers();
      toast.success("Roster restored", {
        description: `${seedRes.added || 0} added, ${seedRes.skipped || 0} skipped · ${refRes.refreshed || 0} refreshed`,
      });
      await load();
    } catch (e) {
      toast.error("Restore failed", { description: e?.response?.data?.detail || e.message });
    } finally { setSeeding(false); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove ${p.name}?`)) return;
    try { await api.deletePlayer(p.id); toast.success("Removed"); await load(); }
    catch { toast.error("Delete failed"); }
  };

  const filteredSorted = useMemo(() => {
    let rows = players;
    if (league !== "ALL") rows = rows.filter(p => p.league === league);
    if (teamFilter !== "ALL") rows = rows.filter(p => p.team === teamFilter);
    if (posFilter !== "ALL") rows = rows.filter(p => (p.position || "").toUpperCase() === posFilter);
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      rows = rows.filter(p => p.name.toLowerCase().includes(q) || (p.team || "").toLowerCase().includes(q));
    }
    const col = COLUMNS.find(c => c.key === sortKey);
    if (col) {
      const dir = sortDir === "asc" ? 1 : -1;
      rows = [...rows].sort((a, b) => {
        const va = col.get(a, averages[a.id], trending?.by_id?.[a.id]);
        const vb = col.get(b, averages[b.id], trending?.by_id?.[b.id]);
        if (va < vb) return -1 * dir;
        if (va > vb) return  1 * dir;
        return 0;
      });
    }
    return rows;
  }, [players, averages, trending, league, teamFilter, posFilter, search, sortKey, sortDir]);

  // Group by training placement when the "ALL" tab is active so Maximize → Program Platinum → Workouts → Unassigned stack visually.
  const sectionedRows = useMemo(() => {
    if (league !== "ALL") return null;
    const byLevel = new Map(TRAINING_SECTIONS.map(s => [s.key, []]));
    for (const p of filteredSorted) {
      const key = p.training_level && byLevel.has(p.training_level) ? p.training_level : "__UNASSIGNED__";
      byLevel.get(key).push(p);
    }
    return TRAINING_SECTIONS
      .map(s => ({ ...s, players: byLevel.get(s.key) || [] }))
      .filter(s => s.players.length > 0);
  }, [filteredSorted, league]);

  const teams = useMemo(() => Array.from(new Set(players.map(p => p.team).filter(Boolean))).sort(), [players]);
  const positions = useMemo(() => Array.from(new Set(players.map(p => (p.position || "").toUpperCase()).filter(Boolean))).sort(), [players]);

  const kpis = useMemo(() => {
    const rows = players.map(p => averages[p.id]).filter(Boolean);
    const games = rows.reduce((s, r) => s + (Number(r.games_played) || 0), 0);
    return { count: players.length, games };
  }, [players, averages]);

  // Per-stat movers moved to /trending. Dashboard just needs `trending?.by_id`
  // to power the per-row trend arrows in the table.

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir(["name", "team"].includes(key) ? "asc" : "desc"); }
  };
  const toggleSelect = (p) => setSelected(prev => prev.includes(p.id) ? prev.filter(id => id !== p.id) : (prev.length >= 5 ? (toast.error("Max 5"), prev) : [...prev, p.id]));
  const startCompare = () => selected.length >= 2 ? navigate(`/compare?ids=${selected.join(",")}`) : toast.error("Pick ≥ 2 players");

  return (
    <div className="min-h-screen">
      <Navbar
        onAddPlayer={admin ? () => setAddOpen(true) : undefined}
        right={
          <>
            {admin && <BackupControls onImportDone={load} onRestoreRoster={runSeed} restoringRoster={seeding} />}
            <button
              onClick={() => { setCompareMode(v => !v); setSelected([]); }}
              className={`flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-4 h-9 sm:py-2 text-[10px] sm:text-xs uppercase tracking-[0.25em] font-bold border ${compareMode ? "bg-[var(--org-accent)] text-[#121212] border-[var(--org-accent)]" : "border-white/20 text-white hover:border-[var(--org-accent)] hover:text-[var(--org-accent)]"}`}
              style={{ transition: "background-color 0.2s ease, color 0.2s ease, border-color 0.2s ease" }}
              data-testid="toggle-compare-mode"
            >
              <GitCompareArrows className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              <span className="hidden sm:inline">{compareMode ? "Cancel" : "Compare"}</span>
            </button>
          </>
        }
      />

      {/* KPI + section navigation strip */}
      <section className="border-b border-white/10" data-testid="kpi-strip">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8 space-y-6">
          <div>
            <h1 className="font-heading text-3xl md:text-4xl leading-none tracking-tight text-white uppercase" data-testid="dashboard-title">
              Player Tracker Dashboard
            </h1>
            <div className="mt-1 h-[2px] w-16 bg-[var(--org-accent)]" />
          </div>
          <div className="flex flex-wrap items-stretch justify-between gap-6">
            <div className="flex items-end gap-10 md:gap-14">
              <Kpi label="Players Tracked" value={kpis.count} testid="kpi-players" />
              <Kpi label="Games Logged"   value={kpis.games}  testid="kpi-games" />
            </div>
            <div className="flex flex-wrap gap-3">
              <PageNavCard variant="player-tracker" active players={players} />
              <PageNavCard variant="coach" to="/coach" />
              <PageNavCard variant="trending" to="/trending" />
            </div>
          </div>
        </div>
      </section>

      {/* Filters row */}
      <section className="border-b border-white/10 sticky top-14 sm:top-16 z-30 bg-[#0a0a0a]/80 backdrop-blur-xl">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-4 flex flex-col md:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#a0a0a0]" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search player…"
              className="w-full pl-10 pr-3 h-11 bg-[#1c1c1c] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]"
              data-testid="dashboard-search" />
          </div>
          <select value={season || (seasons[0]?.key ?? "")} onChange={(e) => setSeason(e.target.value === seasons[0]?.key ? null : e.target.value)}
            className={`h-11 px-3 bg-[#1c1c1c] border text-sm font-mono focus:outline-none focus:border-[var(--org-accent)] ${pastSeason ? "border-[var(--org-accent)] text-[var(--org-accent)]" : "border-white/10 text-white"}`} data-testid="season-select" title="Season">
            {seasons.map((s, i) => <option key={s.key} value={s.key}>{s.label}{i === 0 ? " · current" : ""}</option>)}
          </select>
          <div className="flex flex-wrap gap-2">
            {LEAGUES.map(l => (
              <button key={l} onClick={() => setLeague(l)} data-testid={`tab-${l.toLowerCase()}`}
                className={`px-3 sm:px-4 h-11 text-[11px] uppercase tracking-[0.25em] font-bold border ${league === l ? "bg-[var(--org-accent)] text-[#121212] border-[var(--org-accent)]" : "border-white/10 text-[#a0a0a0] hover:text-white"}`}
                style={{ transition: "background-color 0.15s ease, color 0.15s ease" }}>
                {LEAGUE_LABEL[l] || l}
              </button>
            ))}
          </div>
          <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}
            className="h-11 px-3 bg-[#1c1c1c] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]" data-testid="filter-team">
            <option value="ALL">All Teams</option>
            {teams.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
          <select value={posFilter} onChange={(e) => setPosFilter(e.target.value)}
            className="h-11 px-3 bg-[#1c1c1c] border border-white/10 text-white text-sm focus:outline-none focus:border-[var(--org-accent)]" data-testid="filter-position">
            <option value="ALL">All Positions</option>
            {positions.map(p => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>
      </section>

      {/* Table */}
      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 py-8">
        {pastSeason && (
          <div className="mb-6 border border-[var(--org-accent)]/40 bg-[var(--org-accent)]/5 px-4 py-3 flex flex-wrap items-center gap-3 text-sm text-[#d0d0d0]" data-testid="past-season-banner">
            <span className="text-[10px] uppercase tracking-[0.3em] font-bold text-[var(--org-accent)]">{pastSeason.label} season</span>
            <span>Averages and teams are as of that season · trend arrows only apply to the current season.</span>
            <button onClick={() => setSeason(null)} className="ml-auto text-[10px] uppercase tracking-[0.25em] font-bold text-white hover:text-[var(--org-accent)]" data-testid="past-season-back">Back to current →</button>
          </div>
        )}
        {loading ? (
          <div className="h-96 border border-white/10 bg-[#1c1c1c] animate-pulse" data-testid="dashboard-loading" />
        ) : players.length === 0 ? (
          <EmptyState onSeed={runSeed} onAdd={() => setAddOpen(true)} seeding={seeding} />
        ) : (
          <div className="border border-white/10 overflow-x-auto" data-testid="players-table">
            <table className="w-full text-sm">
              <thead className="bg-[#101010] text-[10px] uppercase tracking-[0.2em] text-[#a0a0a0]">
                <tr>
                  {compareMode && <th className="px-3 py-3 border-b border-white/10 w-8 sticky left-0 bg-[#101010] z-10"></th>}
                  {COLUMNS.map((c, ci) => (
                    <th key={c.key} className={`px-4 py-3 border-b border-white/10 font-bold whitespace-nowrap text-${c.align} ${c.sortable ? "cursor-pointer select-none hover:text-[var(--org-accent)]" : ""} ${ci === 0 ? `sticky ${compareMode ? "left-11" : "left-0"} bg-[#101010] z-10` : ""}`}
                      onClick={c.sortable ? () => toggleSort(c.key) : undefined}
                      data-testid={`col-${c.key}`}
                      style={{ transition: "color 0.15s ease" }}>
                      <span className="inline-flex items-center gap-1">
                        {c.label}
                        {c.sortable && (
                          sortKey === c.key ? <span className="text-[var(--org-accent)]">{sortDir === "desc" ? "↓" : "↑"}</span> : <ArrowUpDown className="w-3 h-3 opacity-40" />
                        )}
                      </span>
                    </th>
                  ))}
                  <th className="px-3 py-3 border-b border-white/10 w-8"></th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const totalCols = COLUMNS.length + (compareMode ? 2 : 1);
                  const renderPlayerRow = (p, i) => {
                    const a = averages[p.id] || {};
                    const hs = headshotFor(p);
                    const isSel = selected.includes(p.id);
                    const bg = i % 2 === 0 ? "bg-[#1c1c1c]" : "bg-[#101010]";
                    return (
                      <tr key={p.id} className={`${bg} hover:bg-[#262626] group cursor-pointer`}
                          style={{ transition: "background-color 0.15s ease" }}
                          onClick={() => compareMode ? toggleSelect(p) : navigate(`/home/${p.id}`)}
                          data-testid={`row-${p.id}`}>
                        {compareMode && (
                          <td className={`px-3 py-2 border-b border-white/5 sticky left-0 z-10 ${bg}`} onClick={(e) => { e.stopPropagation(); toggleSelect(p); }}>
                            <div className={`w-5 h-5 border-2 flex items-center justify-center ${isSel ? "bg-[var(--org-accent)] border-[var(--org-accent)]" : "border-white/30"}`}>
                              {isSel && <span className="text-[#121212] text-xs font-bold">✓</span>}
                            </div>
                          </td>
                        )}
                        <td className={`px-4 py-2 border-b border-white/5 sticky ${compareMode ? "left-11" : "left-0"} z-10 ${bg}`}>
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 border border-white/10 bg-[#101010] flex items-center justify-center overflow-hidden shrink-0">
                              {hs ? <img src={hs} alt={p.name} className="w-full h-full object-cover object-top" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                                  : <span className="text-[10px] text-[var(--org-accent)] font-bold">{initials(p.name)}</span>}
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <div className="font-heading text-base text-white leading-tight uppercase truncate">{p.name}</div>
                                {p.training_level && (
                                  <TrainingLevelPill level={p.training_level} subTier={p.training_sub_tier} size="sm" compact testId={`training-pill-${p.id}`} />
                                )}
                                {p.league === "HS" && p.bio?.package_name && !p.training_level && (
                                  <PackageBadge name={p.bio.package_name} status={p.bio.package_status} testId={`package-badge-${p.id}`} />
                                )}
                              </div>
                              <div className="flex items-center gap-2 mt-0.5">
                                <span className={`text-[9px] font-bold tracking-widest ${p.league === "NBA" ? "text-[var(--org-accent)]" : "text-[#60a5fa]"}`}>{p.league}</span>
                                {p.jersey_number && <span className="text-[9px] text-[#757575] font-mono">#{p.jersey_number}</span>}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2 border-b border-white/5 text-[#a0a0a0] whitespace-nowrap">{p.team || "—"}</td>
                        <td className="px-4 py-2 border-b border-white/5 text-[#a0a0a0]">{p.position || "—"}</td>
                        <td className="px-4 py-2 border-b border-white/5 text-right font-mono text-white" data-testid={`gp-${p.id}`}>{a.games_played ?? "—"}</td>
                        <StatCell value={a.min} display={fmtMin(a.min)} recent={trending?.by_id?.[p.id]?.recent_min} isMin testid={`min-${p.id}`} />
                        <StatCell value={a.pts} display={fmtNum(a.pts)} recent={trending?.by_id?.[p.id]?.recent_pts} accent testid={`pts-${p.id}`} />
                        <StatCell value={a.reb} display={fmtNum(a.reb)} recent={trending?.by_id?.[p.id]?.recent_reb} testid={`reb-${p.id}`} />
                        <StatCell value={a.ast} display={fmtNum(a.ast)} recent={trending?.by_id?.[p.id]?.recent_ast} testid={`ast-${p.id}`} />
                        <StatCell value={a.fg_pct} display={fmtPct(a.fg_pct)} recent={trending?.by_id?.[p.id]?.recent_fg_pct} isPct testid={`fg-${p.id}`} />
                        <StatCell value={a.fg3_pct} display={fmtPct(a.fg3_pct)} recent={trending?.by_id?.[p.id]?.recent_fg3_pct} isPct testid={`fg3-${p.id}`} />
                        <StatCell value={a.ft_pct} display={fmtPct(a.ft_pct)} recent={trending?.by_id?.[p.id]?.recent_ft_pct} isPct testid={`ft-${p.id}`} />
                        <td className="px-3 py-2 border-b border-white/5 text-right" onClick={(e) => e.stopPropagation()}>
                          {admin && (
                            <button onClick={() => remove(p)} className="opacity-0 group-hover:opacity-100 text-[#757575] hover:text-red-500 text-xs"
                              style={{ transition: "opacity 0.15s ease, color 0.15s ease" }} data-testid={`delete-player-${p.id}`}>
                              <X className="w-4 h-4" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  };

                  if (sectionedRows) {
                    // ALL tab — render each training-level section with its own header row
                    if (sectionedRows.length === 0) {
                      return <tr><td colSpan={totalCols} className="px-4 py-8 text-center text-[#757575] text-sm">No players match those filters</td></tr>;
                    }
                    const out = [];
                    sectionedRows.forEach(section => {
                      out.push(
                        <tr key={`sec-${section.key}`} data-testid={`section-${section.key}`}>
                          <td colSpan={totalCols} className="px-4 py-3 border-b border-t border-white/10 bg-[#181818]">
                            <div className="flex items-center justify-between gap-4">
                              <div className="flex items-center gap-3 min-w-0">
                                <span
                                  className="inline-block w-1 h-6 shrink-0"
                                  style={{ background: section.accent }}
                                />
                                <div className="min-w-0">
                                  <div className="font-heading text-sm md:text-base uppercase tracking-[0.2em] text-white leading-tight truncate">
                                    {section.label}
                                  </div>
                                  <div className="text-[10px] uppercase tracking-[0.25em] mt-0.5 truncate" style={{ color: `${section.accent}dd` }}>
                                    {section.description}
                                  </div>
                                </div>
                              </div>
                              <div
                                className="text-[10px] uppercase tracking-[0.3em] font-bold px-2 py-1 shrink-0"
                                style={{ color: section.accent, background: `${section.accent}12`, border: `1px solid ${section.accent}55` }}
                                data-testid={`section-count-${section.key}`}
                              >
                                {section.players.length} {section.players.length === 1 ? "player" : "players"}
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                      section.players.forEach((p, i) => out.push(renderPlayerRow(p, i)));
                    });
                    return out;
                  }

                  // Non-ALL tab — plain flat rendering
                  if (filteredSorted.length === 0) {
                    return <tr><td colSpan={totalCols} className="px-4 py-8 text-center text-[#757575] text-sm">No players match those filters</td></tr>;
                  }
                  return filteredSorted.map((p, i) => renderPlayerRow(p, i));
                })()}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <AnimatePresence>
        {compareMode && (
          <motion.div initial={{ y: 100, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 100, opacity: 0 }}
            transition={{ type: "spring", damping: 20, stiffness: 200 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-[#1c1c1c] border border-[var(--org-accent)] hard-shadow-brand" data-testid="compare-bar">
            <div className="flex items-center gap-6 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="font-heading text-4xl leading-none text-[var(--org-accent)]" data-testid="compare-count">{String(selected.length).padStart(2, "0")}</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#a0a0a0]">Selected<br />(2-5 to compare)</div>
              </div>
              <div className="h-10 w-px bg-white/10" />
              <button onClick={() => setSelected([])} disabled={selected.length === 0}
                className="text-xs uppercase tracking-[0.25em] text-[#a0a0a0] hover:text-white disabled:opacity-30 flex items-center gap-1"
                style={{ transition: "color 0.2s ease" }} data-testid="compare-clear">
                <X className="w-3 h-3" /> Clear
              </button>
              <button onClick={startCompare} disabled={selected.length < 2}
                className="flex items-center gap-2 bg-[var(--org-accent)] text-[#121212] px-5 py-2 text-xs uppercase tracking-[0.25em] font-bold disabled:opacity-40 hover:bg-[var(--org-accent-hover)]"
                style={{ transition: "background-color 0.2s ease" }} data-testid="compare-go">
                Compare <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AddPlayerDialog open={addOpen} onOpenChange={setAddOpen} onAdded={load} />
    </div>
  );
}

function Kpi({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="font-heading text-5xl text-[var(--org-accent)] stat-glow leading-none">{value}</div>
      <div className="text-[10px] tracking-[0.3em] uppercase text-[#a0a0a0] mt-2">{label}</div>
    </div>
  );
}

function StatCell({ value, display, recent, isPct, isMin, accent, testid }) {
  // Compare recent-5-game average against season average for this stat.
  // Threshold ignores tiny drift (<3% relative or <0.1 counting) so arrows stay meaningful.
  const seasonNum = isMin ? minToNumber(value) : Number(value);
  const recentNum = Number(recent);
  const showArrow = Number.isFinite(seasonNum) && Number.isFinite(recentNum) && seasonNum !== 0;
  let dir = "flat";
  if (showArrow) {
    const delta = recentNum - seasonNum;
    const absTol = isPct ? 0.005 : (isMin ? 0.5 : 0.15);
    if (delta > absTol) dir = "up";
    else if (delta < -absTol) dir = "down";
  }
  const arrow = dir === "up"
    ? <TrendingUp className="w-3 h-3 text-[#4ade80] shrink-0" />
    : dir === "down"
      ? <TrendingDown className="w-3 h-3 text-[#f87171] shrink-0" />
      : null;
  const title = showArrow
    ? `Last 5: ${isPct ? `${(recentNum * 100).toFixed(1)}%` : recentNum.toFixed(isMin ? 1 : 1)} · Season: ${display}`
    : undefined;
  return (
    <td className="px-4 py-2 border-b border-white/5 text-right" data-testid={testid} title={title}>
      <div className="inline-flex items-center gap-1.5 justify-end">
        {arrow}
        <span className={accent ? "font-heading text-lg text-[var(--org-accent)]" : "font-mono text-white"}>{display}</span>
      </div>
    </td>
  );
}

function EmptyState({ onSeed, onAdd, seeding }) {
  return (
    <div className="border border-white/10 bg-[#1c1c1c] p-12 text-center" data-testid="empty-state">
      <div className="font-heading text-4xl text-white mb-3 uppercase">No players yet</div>
      <p className="text-[#a0a0a0] max-w-md mx-auto mb-8">Add players to start tracking season averages, game logs and trends.</p>
      <div className="flex items-center justify-center gap-3">
        <button onClick={onSeed} disabled={seeding} className="border border-[var(--org-accent)] text-[var(--org-accent)] px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold hover:bg-[var(--org-accent)]/10 disabled:opacity-50"
          style={{ transition: "background-color 0.2s ease" }} data-testid="empty-seed-btn">
          <Zap className="w-3 h-3 inline mr-2" /> {seeding ? "Loading…" : "Load starter roster"}
        </button>
        <button onClick={onAdd} className="bg-[var(--org-accent)] text-[#121212] px-5 py-3 text-xs uppercase tracking-[0.25em] font-bold hover:bg-[var(--org-accent-hover)]"
          style={{ transition: "background-color 0.2s ease" }} data-testid="empty-add-btn">
          <Plus className="w-3 h-3 inline mr-2" /> Add Player
        </button>
      </div>
    </div>
  );
}
